/**
 * Knowledge Base API service.
 * Pure async functions — no React state, no hooks.
 */
import client, { get, del, post, upload } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import { fetchAllPages, normalizePaginationParams } from '@core/api/pagination';
import type { PaginatedResponse, PaginationParams } from '@core/api/types';

// ── Domain types ──────────────────────────────────────────────────────────────

export type DocumentStatus = 'processing' | 'ready' | 'failed';

export interface IndexingProgress {
  stage: string;
  percent: number;
  message?: string;
  chunkCount?: number;
  embeddedCount?: number;
  updatedAt: string;
}

export interface KnowledgeDocument {
  id: string;
  agentId: string;
  originalFilename: string;
  mimeType: string;
  fileSizeBytes: number;
  status: DocumentStatus;
  source?: 'file' | 'manual';
  chunkCount?: number;
  checksumSha256?: string;
  indexingProgress?: IndexingProgress;
  errorMessage?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface DocumentStatusResponse {
  documentId: string;
  status: DocumentStatus;
  indexingProgress?: IndexingProgress;
  chunkCount?: number;
  errorMessage?: string;
}

export interface DocumentViewUrl {
  /** Signed, short-lived link to the original file (inline where supported). */
  url: string;
  expiresAt: string;
  filename: string;
  mimeType: string;
}

export interface UploadDocumentOptions {
  /** Called with 0–100 as the upload progresses. */
  onProgress?: (percent: number) => void;
  /** AbortSignal to cancel the upload mid-stream. */
  signal?: AbortSignal;
}

/**
 * Local-storage links are served by the API itself (`/storage/local-files`).
 * The backend builds them from its own idea of its public URL, which on a
 * server without STORAGE_LOCAL_PUBLIC_BASE_URL (or behind a proxy that hides
 * the public host) comes out as `http://localhost:<port>` — unreachable from
 * the visitor's browser. This app already knows where the API really is (it is
 * talking to it), so rebase such links onto that origin. S3 presigned URLs
 * (any other host/path) are returned untouched.
 */
export function rebaseLocalFileUrl(url: string): string {
  try {
    const link = new URL(url);
    if (!link.pathname.endsWith('/storage/local-files')) return url;
    const apiBase = new URL(
      client.defaults.baseURL ?? '',
      typeof window !== 'undefined' ? window.location.href : undefined,
    );
    if (link.origin === apiBase.origin) return url;
    // Keep the API prefix the backend used (e.g. /api/v1) and everything after.
    return `${apiBase.origin}${link.pathname}${link.search}`;
  } catch {
    return url;
  }
}

// ── Service ───────────────────────────────────────────────────────────────────

export const knowledgeService = {
  /**
   * List all documents for an agent (paginated).
   */
  list(
    agentId: string,
    params?: PaginationParams,
  ): Promise<PaginatedResponse<KnowledgeDocument>> {
    return get(endpoints.knowledge.list(agentId), {
      params: normalizePaginationParams(params),
    });
  },

  /**
   * Fetch all documents for an agent, paginating with limit capped at 100.
   */
  listAll(agentId: string): Promise<KnowledgeDocument[]> {
    return fetchAllPages((page, limit) =>
      knowledgeService.list(agentId, { page, limit }),
    );
  },

  /**
   * Get a single document by ID.
   */
  getById(agentId: string, docId: string): Promise<KnowledgeDocument> {
    return get(endpoints.knowledge.getById(agentId, docId));
  },

  /**
   * Poll the processing status of a document.
   * Useful when implementing a status-polling loop in the UI.
   */
  getStatus(agentId: string, docId: string): Promise<DocumentStatusResponse> {
    return get(endpoints.knowledge.status(agentId, docId));
  },

  /**
   * Upload a new document file for indexing.
   *
   * @param agentId    - Agent that will own this knowledge.
   * @param file       - The File object from the browser.
   * @param options    - onProgress callback and optional AbortSignal.
   */
  upload(
    agentId: string,
    file: File,
    options: UploadDocumentOptions = {},
  ): Promise<KnowledgeDocument> {
    const formData = new FormData();
    formData.append('file', file);
    return upload(
      endpoints.knowledge.upload(agentId),
      formData,
      options.onProgress,
      options.signal,
    );
  },

  /**
   * Create a knowledge document from title + plain text (KB text editor).
   */
  createFromText(
    agentId: string,
    payload: { title: string; content: string },
  ): Promise<KnowledgeDocument> {
    return post(endpoints.knowledge.createText(agentId), payload);
  },

  /**
   * Get a short-lived signed link to open/download the original file.
   */
  async getViewUrl(agentId: string, docId: string): Promise<DocumentViewUrl> {
    const view = await get<DocumentViewUrl>(endpoints.knowledge.viewUrl(agentId, docId));
    return { ...view, url: rebaseLocalFileUrl(view.url) };
  },

  /**
   * Retry processing a failed document without re-uploading the file.
   */
  retry(agentId: string, docId: string): Promise<KnowledgeDocument> {
    return post(endpoints.knowledge.retry(agentId, docId));
  },

  /**
   * Delete a document and remove all its chunks and vectors.
   */
  delete(agentId: string, docId: string): Promise<void> {
    return del(endpoints.knowledge.delete(agentId, docId));
  },
} as const;
